import { useState, useEffect, useRef } from 'react';
import { User, Mail, Phone, Lock, Save, Loader2, Sun, Moon, Building2, Bell, Camera } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import RecordMetadata from '@/components/RecordMetadata';
import { Switch } from '@/components/ui/switch';
import { profileService, companyService } from '@/services';
import { AvatarEditorDialog } from '@/components/profile/AvatarEditorDialog';
import type { AuditedRecord } from '@prolarus/shared';

export default function MyProfile() {
  const { toast } = useToast();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [profileSnapshot, setProfileSnapshot] = useState<Record<string, unknown> | null>(null);
  const [metadata, setMetadata] = useState<AuditedRecord>({
    created: null,
    modified: null,
    created_by_profile: null,
    updated_by_profile: null,
  });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [systemNotify, setSystemNotify] = useState(true);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarPath, setAvatarPath] = useState<string | null>(null);
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      try {
        // Fetch profile data using service
        const result = await profileService.getWithAuditInfo(user.id);
        
        if (result.error) throw result.error;
        
        const data = result.data;
        if (!data) {
          setIsFetching(false);
          return;
        }

        setFormData(prev => ({
          ...prev,
          fullName: data.full_name || '',
          email: user.email || '',
          phone: data.phone || '',
        }));
        setSystemNotify(data.system_notify ?? true);
        setAvatarPath(data.avatar_url);

        // Generate signed URL for avatar if exists
        if (data.avatar_url) {
          const { data: signedUrlData } = await supabase.storage
            .from('avatars')
            .createSignedUrl(data.avatar_url, 3600); // 1 hour expiry
          if (signedUrlData?.signedUrl) {
            setAvatarUrl(signedUrlData.signedUrl);
          }
        }

        // Capture snapshot for history tracking
        setProfileSnapshot({
          full_name: data.full_name,
          phone: data.phone,
          system_notify: data.system_notify,
          avatar_url: data.avatar_url,
        });
        setCompanyId(data.company_id);

        // Fetch company name using service if company_id exists
        if (data.company_id) {
          const { data: companyName } = await companyService.getDisplayName(data.company_id);
          setCompanyName(companyName);
        }

        setMetadata({
          created: data.created ?? null,
          modified: data.modified ?? null,
          created_by_profile: data.created_by_profile ?? null,
          updated_by_profile: data.updated_by_profile ?? null,
        });
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast({
          title: 'Error',
          description: 'Failed to load profile data.',
          variant: 'destructive',
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchProfile();
  }, [user, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setIsLoading(true);

    try {
      if (!profileSnapshot || !companyId) {
        throw new Error('Profile data not loaded properly');
      }

      // Update profile using service with history tracking
      const result = await profileService.updateCurrentProfile(
        {
          full_name: formData.fullName,
          phone: formData.phone,
          system_notify: systemNotify,
          avatar_url: avatarPath,
        },
        profileSnapshot,
        { companyId, userId: user.id }
      );

      if (result.error) throw result.error;

      // Refresh snapshot to new baseline
      setProfileSnapshot({
        full_name: formData.fullName,
        phone: formData.phone,
        system_notify: systemNotify,
        avatar_url: avatarPath,
      });

      // Handle password change if provided
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          toast({
            title: 'Error',
            description: 'New passwords do not match',
            variant: 'destructive',
          });
          setIsLoading(false);
          return;
        }

        const { error: passwordError } = await supabase.auth.updateUser({
          password: formData.newPassword,
        });

        if (passwordError) throw passwordError;
      }

      toast({
        title: 'Profile Updated',
        description: 'Your profile has been saved successfully.',
      });

      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid File',
          description: 'Please select an image file.',
          variant: 'destructive',
        });
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Please select an image smaller than 10MB.',
          variant: 'destructive',
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result as string);
        setAvatarEditorOpen(true);
      };
      reader.readAsDataURL(file);
    }
    // Reset the input so the same file can be selected again
    e.target.value = '';
  };

  const handleAvatarSaved = async (newAvatarPath: string) => {
    setAvatarPath(newAvatarPath);
    
    // Generate signed URL for the new avatar
    const { data: signedUrlData } = await supabase.storage
      .from('avatars')
      .createSignedUrl(newAvatarPath, 3600);
    if (signedUrlData?.signedUrl) {
      setAvatarUrl(signedUrlData.signedUrl);
    }

    // Update profile with new avatar path
    if (user && companyId && profileSnapshot) {
      await profileService.updateCurrentProfile(
        { avatar_url: newAvatarPath },
        profileSnapshot,
        { companyId, userId: user.id }
      );
      
      // Update snapshot
      setProfileSnapshot((prev) => prev ? { ...prev, avatar_url: newAvatarPath } : null);
    }

    // Dispatch event to notify other components (like UserAvatarMenu)
    window.dispatchEvent(new CustomEvent('avatar-updated', { 
      detail: { avatarPath: newAvatarPath } 
    }));
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="animate-slide-up max-w-4xl">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">My Profile</h1>
        <p className="page-subtitle">Manage your personal information and preferences.</p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Profile Avatar & Basic Info */}
        <div className="stat-card mb-6">
          <div className="flex items-start gap-6 mb-6">
            <button
              type="button"
              onClick={handleAvatarClick}
              className="relative w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={formData.fullName || 'Profile'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-3xl font-bold text-primary">{getInitials(formData.fullName)}</span>
              )}
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="w-6 h-6 text-white" />
              </div>
            </button>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-xl font-semibold text-foreground">{formData.fullName || 'Your Name'}</h2>
                {companyName && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                    <Building2 className="w-3.5 h-3.5" />
                    {companyName}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground">{formData.email}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="input-field pl-12"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field pl-12"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input-field pl-12"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Appearance Settings */}
        <div className="stat-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              {theme === 'dark' ? (
                <Moon className="w-6 h-6 text-primary" />
              ) : (
                <Sun className="w-6 h-6 text-primary" />
              )}
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Appearance</h2>
              <p className="text-sm text-muted-foreground">Customize how Prolarus looks for you</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
            <div>
              <p className="font-medium text-foreground">Theme</p>
              <p className="text-sm text-muted-foreground">
                Switch between light and dark mode theme
              </p>
            </div>
            <button
              type="button"
              onClick={toggleTheme}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border hover:bg-card/80 transition-colors"
            >
              {theme === 'dark' ? (
                <>
                  <Moon className="w-5 h-5 text-primary" />
                  <span className="text-foreground">Dark</span>
                </>
              ) : (
                <>
                  <Sun className="w-5 h-5 text-primary" />
                  <span className="text-foreground">Light</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="stat-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Bell className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Notifications</h2>
              <p className="text-sm text-muted-foreground">Manage your notification preferences</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
            <div>
              <p className="font-medium text-foreground">System Notifications</p>
              <p className="text-sm text-muted-foreground">
                Receive notifications about system updates and important events
              </p>
            </div>
            <Switch
              checked={systemNotify}
              onCheckedChange={setSystemNotify}
            />
          </div>
        </div>

        {/* Change Password */}
        <div className="stat-card mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground">Change Password</h2>
              <p className="text-sm text-muted-foreground">Update your security credentials</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Current Password
              </label>
              <input
                type="password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                className="input-field"
                placeholder="••••••••"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="••••••••"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button type="submit" disabled={isLoading} className="btn-primary">
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {/* Record Metadata */}
        <RecordMetadata record={metadata} />
      </form>

      {/* Avatar Editor Dialog */}
      {selectedImage && user && companyId && (
        <AvatarEditorDialog
          open={avatarEditorOpen}
          onOpenChange={setAvatarEditorOpen}
          imageSrc={selectedImage}
          userId={user.id}
          companyId={companyId}
          onAvatarSaved={handleAvatarSaved}
        />
      )}
    </div>
  );
}
